import { buildWhenGeofenceBlock } from '../geofence/buildWhenGeofenceBlock';
import * as meshbot from 'constants/MeshbotConstant';
import { convertEventKindToDictionaryOperation } from '../geofence/convertEventKindToDictionaryOperation';
import { GEOFENCES_ALL_GEOFENCES } from 'containers/Ezlo/EzloMeshbot/constants';
import { isUserGeofenceDevice } from 'containers/Ezlo/EzloMeshbot/utils/isUserGeofenceDevice';

/**
 * Converts a geofence "when" block from UI format into an API-ready block for MeshBot.
 *
 * This function finds the corresponding device and geofence item,
 * builds a new "when" block structure, and returns a transformed block.
 *
 * Throws an error if the user device or geofences item cannot be found.
 *
 * @param {Object} params - The parameters.
 * @param {Object} params.whenBlock - The original "when" block from the UI.
 * @param {Object} params.controllerData - The controller data.
 * @param {Array<Object>} params.controllerData.devices - List of devices, each containing at least a `name` and `_id` property.
 * @param {Array<Object>} params.controllerData.items -List of device items, each containing at least a `deviceId` and `name` property.
 * @returns {Object} A new "when" block object transformed into the API-required format.
 *
 * @throws {Error} If the user device is not found in `devices`.
 * @throws {Error} If the geofences item is not found for the user's device in `items`.
 *
 * @example
 * const apiBlock = convertGeofenceUiToApiBlock(whenBlock, devices, items);
 * // apiBlock will have updated blockType, blockOptions, fields, and blockMeta
 */
export const geofenceStrategy = ({ whenBlock, controllerData: { devices = [], items = [] } }) => {
    const { location, user, eventKind } = whenBlock?.blockMeta || {};
    const userAbstractDevice = devices.find((device) => isUserGeofenceDevice(device, user));
    if (!userAbstractDevice) {
        throw new Error(`Abstract device for user "${user}" not found.`);
    }
    const geofencesItem = items.find(
        (item) => item.deviceId === userAbstractDevice._id && item.name === GEOFENCES_ALL_GEOFENCES,
    );

    if (!geofencesItem) {
        throw new Error(`Geofences item for device "${userAbstractDevice._id}" not found.`);
    }

    const whenGeofenceBlock = buildWhenGeofenceBlock(
        geofencesItem._id,
        location,
        convertEventKindToDictionaryOperation(eventKind),
    );

    return {
        ...whenBlock,
        ...whenGeofenceBlock,
        blockMeta: { nodeType: meshbot.MESHBOT_NODE_TYPES.GEOFENCE },
    };
};
